import clsx from 'clsx';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '@gitbook/spine-icons';
import { Button, type ButtonKind, type ButtonProps, type ButtonSize } from '@gitbook/spine-react';

import { createPalette, PaletteButton, type PaletteDef } from '../Palette';

type ButtonPressHandler = NonNullable<ButtonProps['onPress']>;
type ButtonPressEvent = Parameters<ButtonPressHandler>[0];

type ResponsiveRowButtonBase = {
    key: string;
    inPalette?: boolean;
    canOverflow?: boolean;
    /**
     * Optional hook to wrap the rendered button in extra UI (e.g., a popover).
     * The provided trigger already uses the row-managed kind/size props.
     */
    wrap?: (trigger: React.ReactElement) => React.ReactNode;
};

export type ResponsiveRowButton = ResponsiveRowButtonBase &
    Omit<ButtonProps, 'kind' | 'size' | 'onPress'> & {
        /**
         * onPress callback with an optional event in case we open it from the palette
         */
        onPress: (event?: ButtonPressEvent) => void;
    };

export interface ResponsiveButtonsRowProps {
    /** Ordered list of buttons to render */
    buttons: ResponsiveRowButton[];
    /** Optional className for the root container */
    className?: string;
    /** Kind of buttons */
    kind?: ButtonKind;
    /** Size of the buttons */
    size?: ButtonSize;
}

/**
 * Row of buttons that automatically moves overflowing items into a "More" menu.
 */
export const ResponsiveButtonsRow = React.memo(function ResponsiveButtonsRow(
    props: ResponsiveButtonsRowProps
) {
    const { buttons, className, kind, size } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const prevWidthRef = useRef(0);

    const normalizedButtons = useMemo(
        () =>
            buttons.filter(
                (button): button is ResponsiveRowButton => button !== undefined && button !== null
            ),
        [buttons]
    );

    const layoutEntries = useMemo(
        () =>
            normalizedButtons
                // Track the original index so we can preserve ordering.
                .map((button, index) => ({ button, index }))
                // Buttons explicitly marked for the palette never appear inline.
                .filter(({ button }) => !button.inPalette),
        [normalizedButtons]
    );

    const paletteEntries = useMemo(
        () => normalizedButtons.filter((button) => button.inPalette),
        [normalizedButtons]
    );

    /**
     * Buttons that are allowed to move into the overflow menu when space runs out.
     */
    const overflowEligibleEntries = useMemo(
        () => layoutEntries.filter(({ button }) => button.canOverflow !== false),
        [layoutEntries]
    );

    /**
     * Indexes of buttons that must always stay inline (cannot overflow).
     */
    const alwaysVisibleIndexes = useMemo(
        () =>
            new Set(
                layoutEntries
                    .filter(({ button }) => button.canOverflow === false)
                    .map(({ index }) => index)
            ),
        [layoutEntries]
    );

    // Number of overflow-eligible buttons currently visible inline.
    const [visibleOverflowCount, setVisibleOverflowCount] = useState(
        overflowEligibleEntries.length
    );

    // Reset the visible count when the buttons change
    useEffect(() => {
        setVisibleOverflowCount(overflowEligibleEntries.length);
    }, [overflowEligibleEntries.length]);

    // Update the visible count when the window is resized
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const handleResize = () => {
            const width = el.clientWidth;
            const prevWidth = prevWidthRef.current;
            prevWidthRef.current = width;

            if (overflowEligibleEntries.length === 0) {
                if (visibleOverflowCount !== 0) {
                    setVisibleOverflowCount(0);
                }
                return;
            }

            // If the container grew, try to bring back overflowed buttons.
            if (width > prevWidth && visibleOverflowCount < overflowEligibleEntries.length) {
                setVisibleOverflowCount(overflowEligibleEntries.length);
                return;
            }

            const lastChild = el.children[el.children.length - 1];
            if (!lastChild) {
                return;
            }

            // Hide one button if we detect overflow.
            if (lastChild.getBoundingClientRect().right > el.getBoundingClientRect().right) {
                setVisibleOverflowCount((prev) => Math.max(0, prev - 1));
            }
        };

        handleResize();

        const observer = new ResizeObserver(() => {
            // Defer to the next frame to ensure layout information is up to date.
            requestAnimationFrame(handleResize);
        });
        observer.observe(el);

        return () => {
            observer.disconnect();
        };
    }, [overflowEligibleEntries.length, visibleOverflowCount]);

    /**
     * Overflow-eligible entries that are currently visible inline.
     */
    const visibleOverflowIndexes = useMemo(
        () =>
            new Set(
                overflowEligibleEntries.slice(0, visibleOverflowCount).map((item) => item.index)
            ),
        [overflowEligibleEntries, visibleOverflowCount]
    );

    /**
     * Buttons rendered directly in the row (either because they are pinned or still visible).
     */
    const inlineEntries = useMemo(
        () =>
            layoutEntries.filter(
                ({ index }) => alwaysVisibleIndexes.has(index) || visibleOverflowIndexes.has(index)
            ),
        [layoutEntries, alwaysVisibleIndexes, visibleOverflowIndexes]
    );

    /**
     * Buttons pushed into the overflow palette.
     */
    const overflowEntries = useMemo(
        () =>
            layoutEntries
                .filter(
                    ({ index }) =>
                        !alwaysVisibleIndexes.has(index) && !visibleOverflowIndexes.has(index)
                )
                .map(({ button }) => button),
        [layoutEntries, alwaysVisibleIndexes, visibleOverflowIndexes]
    );

    /**
     * Build the palette configuration for overflow items and buttons that are always in the palette.
     */
    const paletteDef = useMemo(() => {
        if (paletteEntries.length === 0 && overflowEntries.length === 0) {
            return undefined;
        }

        const groups: PaletteDef['groups'] = [];

        if (paletteEntries.length > 0) {
            groups.push({
                key: 'persistent',
                commands: paletteEntries.map((item) => ({
                    key: item.key,
                    title: item.label,
                    icon: item.leadingIcon || item.trailingIcon,
                    trigger: () => item.onPress?.(),
                })),
            });
        }

        if (overflowEntries.length > 0) {
            groups.push({
                key: 'overflow',
                commands: overflowEntries
                    .map((item) => ({
                        key: item.key,
                        title: item.label,
                        icon: item.leadingIcon || item.trailingIcon,
                        trigger: () => item.onPress?.(),
                    }))
                    .reverse(),
            });
        }

        return createPalette({ groups });
    }, [overflowEntries, paletteEntries]);

    return (
        <div
            ref={containerRef}
            className={clsx('flex w-full min-w-0 items-center gap-x-1', className)}
        >
            {inlineEntries.map(({ button }) => {
                const {
                    wrap,
                    key: buttonKey,
                    inPalette: _inPalette,
                    canOverflow: _canOverflow,
                    ...buttonProps
                } = button;
                const trigger = <Button {...buttonProps} kind={kind} size={size} />;
                return (
                    <div key={buttonKey} className="flex-shrink-0">
                        {/* Allow callers to wrap the trigger (for popovers, etc.) without reimplementing layout logic. */}
                        {wrap ? wrap(trigger) : trigger}
                    </div>
                );
            })}

            {paletteDef ? (
                <div className="relative flex-shrink-0">
                    <PaletteButton
                        palette={() => paletteDef}
                        label="More"
                        iconOnly
                        leadingIcon={Icon.MoreHorizontal}
                        kind={kind}
                        size={size}
                    />
                </div>
            ) : null}
        </div>
    );
});
